import React, { useState } from 'react';
import {Link, useNavigate} from 'react-router-dom';
import { useAuth } from "../../contexts/AuthContext";
import { useThemeContext } from "../../contexts/ThemeContext";
import Button from '../../components/general/Button';
import {Helmet} from 'react-helmet';
import ThemeSwitch from '../../components/general/ThemeSwitch';
import QuickNotesModal from '../../components/widgets/quick-notes/QuickNotesModal';
import GeneralFeedback from '../../components/feedback/modals/GeneralFeedback';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faNotes, faBug, faPalette, faLanguage, faUser, faStar, faCog, faSignOut, faPlay } from '@fortawesome/pro-solid-svg-icons';

const MainSettings = () => {
    let {logout} = useAuth();
    const { toggleTheme } = useThemeContext();
    const navigate = useNavigate();
    const [showNotesModal, setShowNotesModal] = useState(false);
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
    
    const logOutClick = async () => {
        try {
            await logout();
            //console.log("success");
            navigate("/admin");
          } catch {
            //console.log("Failed to log out");
          };
    }
    return (
        <>
            <Helmet>
                <title>Settings - Yalango</title>
                <meta name="description" content={"Change your user settings on Yalango."} />
            </Helmet>
            <div className="max-w-6xl mx-auto px-6 py-12">
                    {/* Header */}
                    <div className="text-center mb-12">
                        <h1 className="text-4xl font-bold bg-gradient-to-r p-2 from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">
                            Settings
                        </h1>
                        <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                            Customize your Yalango experience and manage your account preferences
                        </p>
                    </div>

                    {/* Main Settings Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                        {/* Get Started */}
                        <Link to="/get-started" className="group no-underline">
                            <div className="bg-gradient-to-br from-persian-green to-darker-persian-green p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-20 h-20 bg-white bg-opacity-20 rounded-full -translate-y-10 translate-x-10"></div>
                                <div className="absolute bottom-0 left-0 w-16 h-16 bg-white bg-opacity-10 rounded-full translate-y-8 -translate-x-8"></div>
                                <div className="relative z-10">
                                    <div className="flex items-center gap-4 mb-4">
                                        <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                                            <FontAwesomeIcon icon={faPlay} className="text-white text-xl" />
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-semibold text-white">
                                                Get Started
                                            </h3>
                                            <p className="text-white text-opacity-90">Learn how to use Yalango and our features</p>
                                        </div>
                                    </div>
                                    <div className="text-white text-sm font-medium group-hover:underline">
                                        View guide →
                                    </div>
                                </div>
                            </div>
                        </Link>

                        {/* My Languages */}
                        <Link to="/my-languages" className="group no-underline">
                            <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100 dark:border-gray-700">
                                <div className="flex items-center gap-4 mb-4">
                                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                                        <FontAwesomeIcon icon={faLanguage} className="text-white text-xl" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-800 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                            My Languages
                                        </h3>
                                        <p className="text-gray-600 dark:text-gray-400">Manage your learning languages</p>
                                    </div>
                                </div>
                                <div className="text-blue-600 dark:text-blue-400 text-sm font-medium group-hover:underline">
                                    Configure languages →
                                </div>
                            </div>
                        </Link>

                        {/* Profile Settings */}
                        <Link to="/settings/profile" className="group no-underline">
                            <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100 dark:border-gray-700">
                                <div className="flex items-center gap-4 mb-4">
                                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center">
                                        <FontAwesomeIcon icon={faUser} className="text-white text-xl" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-800 dark:text-white group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors">
                                            Profile Settings
                                        </h3>
                                        <p className="text-gray-600 dark:text-gray-400">Update your profile information</p>
                                    </div>
                                </div>
                                <div className="text-green-600 dark:text-green-400 text-sm font-medium group-hover:underline">
                                    Edit profile →
                                </div>
                            </div>
                        </Link>

                        {/* Premium */}
                        <Link to="/current-plan" className="group no-underline">
                            <div className="bg-gradient-to-br from-yellow-400 to-orange-500 p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-20 h-20 bg-white bg-opacity-20 rounded-full -translate-y-10 translate-x-10"></div>
                                <div className="absolute bottom-0 left-0 w-16 h-16 bg-white bg-opacity-10 rounded-full translate-y-8 -translate-x-8"></div>
                                <div className="relative z-10">
                                    <div className="flex items-center gap-4 mb-4">
                                        <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                                            <FontAwesomeIcon icon={faStar} className="text-white text-xl" />
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-semibold text-white">
                                                Yalango Premium
                                            </h3>
                                            <p className="text-white text-opacity-90">Upgrade to premium features</p>
                                        </div>
                                    </div>
                                    <div className="text-white text-sm font-medium group-hover:underline">
                                        Upgrade now →
                                    </div>
                                </div>
                            </div>
                        </Link>

                        {/* Account Settings */}
                        <Link to="/settings/account" className="group no-underline">
                            <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100 dark:border-gray-700">
                                <div className="flex items-center gap-4 mb-4">
                                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                                        <FontAwesomeIcon icon={faCog} className="text-white text-xl" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-800 dark:text-white group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                                            Account Settings
                                        </h3>
                                        <p className="text-gray-600 dark:text-gray-400">Manage your account preferences</p>
                                    </div>
                                </div>
                                <div className="text-purple-600 dark:text-purple-400 text-sm font-medium group-hover:underline">
                                    Configure account →
                                </div>
                            </div>
                        </Link>
                    </div>

                    {/* Utilities Section */}
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
                        <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 px-8 py-6 border-b border-gray-200 dark:border-gray-600">
                            <h2 className="text-2xl font-semibold text-gray-800 dark:text-white flex items-center gap-3">
                                <FontAwesomeIcon icon={faCog} className="text-gray-600 dark:text-gray-400" />
                                Utilities
                            </h2>
                            <p className="text-gray-600 dark:text-gray-400 mt-1">Quick access to essential tools</p>
                        </div>
                        
                        <div className="p-8">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {/* Theme Selector */}
                                <div className="group">
                                    <div className="flex items-center justify-between p-6 bg-gray-50 dark:bg-gray-700 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors cursor-pointer" onClick={toggleTheme}>
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center">
                                                <FontAwesomeIcon icon={faPalette} className="text-white text-sm" />
                                            </div>
                                            <div>
                                                <div className="font-semibold text-gray-800 dark:text-white">Theme</div>
                                                <div className="text-sm text-gray-600 dark:text-gray-400">Light or dark mode</div>
                                            </div>
                                        </div>
                                        <div className="flex items-center justify-center">
                                            <ThemeSwitch />
                                        </div>
                                    </div>
                                </div>

                                {/* Sticky Notes */}
                                <div className="group">
                                    <div className="flex items-center justify-between p-6 bg-gray-50 dark:bg-gray-700 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg flex items-center justify-center">
                                                <FontAwesomeIcon icon={faNotes} className="text-white text-sm" />
                                            </div>
                                            <div>
                                                <div className="font-semibold text-gray-800 dark:text-white">Sticky Notes</div>
                                                <div className="text-sm text-gray-600 dark:text-gray-400">Quick notes & reminders</div>
                                            </div>
                                        </div>
                                        <div className="flex items-center justify-center">
                                            <Button 
                                                text="Open" 
                                                color="green" 
                                                size="small"
                                                onClick={() => setShowNotesModal(true)}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Feedback */}
                                <div className="group">
                                    <div className="flex items-center justify-between p-6 bg-gray-50 dark:bg-gray-700 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-pink-600 rounded-lg flex items-center justify-center">
                                                <FontAwesomeIcon icon={faBug} className="text-white text-sm" />
                                            </div>
                                            <div>
                                                <div className="font-semibold text-gray-800 dark:text-white">Send Feedback</div>
                                                <div className="text-sm text-gray-600 dark:text-gray-400">Help us improve</div>
                                            </div>
                                        </div>
                                        <div className="flex items-center justify-center">
                                            <Button 
                                                text="Send" 
                                                color="green" 
                                                size="small"
                                                onClick={() => setShowFeedbackModal(true)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Logout Section */}
                    <div className="mt-12 flex justify-center">
                        <button type="button" onClick={logOutClick} className="group no-underline bg-transparent border-0 p-0 cursor-pointer">
                            <div className="bg-white dark:bg-gray-800 px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 dark:border-gray-600 hover:border-red-300 dark:hover:border-red-500">
                                <div className="flex items-center justify-center gap-3 text-gray-700 dark:text-gray-300 group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors">
                                    <FontAwesomeIcon icon={faSignOut} className="text-lg" />
                                    <span className="font-semibold">Sign Out</span>
                                </div>
                            </div>
                        </button>
                    </div>
            </div>

            {/* Modals */}
            {showNotesModal && (
                <QuickNotesModal closeFunction={() => setShowNotesModal(false)} />
            )}
            {showFeedbackModal && (
                <GeneralFeedback closeFunction={() => setShowFeedbackModal(false)} />
            )}
        </>
    )
}

export default MainSettings;
